import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Occasion, OccasionType } from '../../../../shared/src/types/database';
import CardDesignPicker from '../cards/CardDesignPicker';
import FontPicker from '../cards/FontPicker';
import { apiFetch } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { DEFAULT_CARD_FONT_ID } from '../../../../shared/src/cardFonts';

const TIER1_ALLOWED: OccasionType[] = ['birthday', 'mothers_day', 'fathers_day', 'valentines'];

/** These types are inherently one-time — default recurring to false */
const ONE_TIME_DEFAULT_TYPES: OccasionType[] = ['thinking_of_you', 'graduation', 'custom'];

const MONTHS = [
  { value: '01', label: 'January' },  { value: '02', label: 'February' },
  { value: '03', label: 'March' },    { value: '04', label: 'April' },
  { value: '05', label: 'May' },      { value: '06', label: 'June' },
  { value: '07', label: 'July' },     { value: '08', label: 'August' },
  { value: '09', label: 'September' },{ value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

interface OccasionFormProps {
  contactId:    string;
  contactName:  string;
  /** Pass false if the contact has no saved mailing address so we can warn the user */
  hasAddress?:  boolean;
  initial?: {
    occasion_type?:        OccasionType;
    occasion_date?:        string;   // YYYY-MM-DD
    recurring?:            boolean;
    custom_occasion_name?: string;
    life_moment_notes?:    string;
    card_greeting?:        string;
    current_message?:      string;
  };
  onSave:    (occasion: Occasion) => void;
  onCancel?: () => void;
  submitLabel?: string;
}

interface OccasionConfig {
  label:       string;
  emoji:       string;
  description: string;
  showYear:    boolean;   // false = just MM-DD (recurring by nature)
}

const OCCASION_TYPES: Record<OccasionType, OccasionConfig> = {
  birthday:       { label: 'Birthday',        emoji: '🎂', description: "Their big day every year",             showYear: true  },
  anniversary:    { label: 'Anniversary',     emoji: '💍', description: 'A relationship milestone',            showYear: true  },
  christmas:      { label: 'Christmas',       emoji: '🎄', description: 'Dec 25 every year',                  showYear: false },
  valentines:     { label: "Valentine's Day", emoji: '💝', description: 'Feb 14 every year',                  showYear: false },
  mothers_day:    { label: "Mother's Day",    emoji: '🌸', description: 'Second Sunday in May',               showYear: false },
  fathers_day:    { label: "Father's Day",    emoji: '👔', description: 'Third Sunday in June',               showYear: false },
  graduation:     { label: 'Graduation',      emoji: '🎓', description: 'One-time or recurring',              showYear: true  },
  thinking_of_you:{ label: 'Thinking of You', emoji: '💌', description: 'Just because, any date',            showYear: true  },
  custom:         { label: 'Custom',          emoji: '✨', description: 'Name your own occasion',             showYear: true  },
};

// Fixed dates for holidays (no year needed — we compute next occurrence)
const FIXED_DATES: Partial<Record<OccasionType, string>> = {
  christmas:   '2000-12-25',
  valentines:  '2000-02-14',
  // mothers_day / fathers_day are computed dynamically — store as an approximate
};

/** Minimum days required for print + mail fulfillment */
const LEAD_DAYS = 14;

/** Preset sign-off closings (the user can also type their own). */
const CLOSING_OPTIONS = [
  'With love,', 'Love,', 'With respect,', 'Respectfully,', 'Sincerely,',
  'Warmly,', 'Thinking of you,', 'From,', 'Cheers,', 'Your friend,',
];

function addDays(base: string, days: number): string {
  const d = new Date(`${base}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toISOString().split('T')[0];
}

function formatDateLong(dateStr: string): string {
  return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-US', {
    month: 'long', day: 'numeric', year: 'numeric',
  });
}

/** Greeting placeholder text per occasion type */
function greetingPlaceholder(type: OccasionType): string {
  switch (type) {
    case 'birthday':       return 'e.g. Happy Birthday, Sarah!';
    case 'anniversary':    return 'e.g. Happy Anniversary!';
    case 'christmas':      return 'e.g. Merry Christmas!';
    case 'valentines':     return 'e.g. Happy Valentine\'s Day!';
    case 'mothers_day':    return 'e.g. Happy Mother\'s Day!';
    case 'fathers_day':    return 'e.g. Happy Father\'s Day!';
    case 'graduation':     return 'e.g. Congratulations on your big day!';
    case 'thinking_of_you':return 'e.g. Thinking of you,';
    case 'custom':         return 'e.g. Congratulations!';
  }
}

function defaultRecurringForType(type: OccasionType, override?: boolean): boolean {
  if (override !== undefined) return override;
  return !ONE_TIME_DEFAULT_TYPES.includes(type);
}

export default function OccasionForm({
  contactId,
  contactName,
  hasAddress,
  initial,
  onSave,
  onCancel,
  submitLabel = 'Save occasion',
}: OccasionFormProps) {
  const { profile, refreshProfile } = useAuth();
  const navigate    = useNavigate();

  const isLocked = (type: OccasionType) =>
    profile?.tier === 1 && !TIER1_ALLOWED.includes(type);

  const [occasionType,       setOccasionType      ] = useState<OccasionType>(initial?.occasion_type ?? 'birthday');
  const [occasionDate,       setOccasionDate      ] = useState(initial?.occasion_date ?? '');
  // Birthday-specific: month/day selects + optional year
  const [birthMonth,         setBirthMonth        ] = useState('');
  const [birthDay,           setBirthDay          ] = useState('');
  const [recurring,          setRecurring         ] = useState(
    defaultRecurringForType(initial?.occasion_type ?? 'birthday', initial?.recurring),
  );
  const [customName,         setCustomName        ] = useState(initial?.custom_occasion_name ?? '');
  const [lifeMomentNotes,    setLifeMomentNotes   ] = useState(initial?.life_moment_notes ?? '');
  const [birthYear,          setBirthYear         ] = useState('');
  const [cardDesignId,       setCardDesignId      ] = useState<string | null>(null);
  const [cardGreeting,       setCardGreeting      ] = useState(initial?.card_greeting ?? '');
  const [currentMessage,     setCurrentMessage    ] = useState(initial?.current_message ?? '');
  const [cardClosing,        setCardClosing       ] = useState(profile?.card_closing ?? 'With love,');
  const [cardFromLine,       setCardFromLine      ] = useState(profile?.card_from_line ?? profile?.first_name ?? '');
  const [cardFontId,         setCardFontId        ] = useState(profile?.card_font_id ?? DEFAULT_CARD_FONT_ID);
  const [saving,             setSaving            ] = useState(false);
  const [error,              setError             ] = useState('');
  const [savedOccasion,      setSavedOccasion     ] = useState<Occasion | null>(null);
  const [scheduleNotice,     setScheduleNotice    ] = useState('');

  const config = OCCASION_TYPES[occasionType];
  const today = new Date().toISOString().split('T')[0];
  const minScheduleDate = addDays(today, LEAD_DAYS);

  // When a fixed-date holiday is selected, fill the date automatically.
  // When switching away from a fixed type, clear the stale placeholder date
  // so it doesn't appear in (and fail validation on) the newly-visible picker.
  function handleTypeChange(type: OccasionType) {
    setOccasionType(type);
    setError('');
    const fixed = FIXED_DATES[type];
    if (fixed) {
      setOccasionDate(fixed);
      setRecurring(true);
    } else {
      setOccasionDate('');
      setRecurring(defaultRecurringForType(type));
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    // For birthday: build occasion_date from month/day selects
    let resolvedDate = occasionDate;
    if (occasionType === 'birthday') {
      if (!birthMonth || !birthDay) { setError('Please choose a birth month and day.'); return; }
      // Use birth year if provided, otherwise 1900 as a neutral placeholder
      const yearPart = birthYear && parseInt(birthYear, 10) > 1900 ? birthYear : '1900';
      resolvedDate = `${yearPart}-${birthMonth}-${birthDay}`;
    }

    if (!resolvedDate) { setError('Please choose a date.'); return; }
    if (occasionType === 'custom' && !customName.trim()) {
      setError('Please name your custom occasion.'); return;
    }
    // Block past dates for occasions where the user picks a specific date.
    // Fixed-date holidays (christmas, valentines) store a placeholder year and
    // the backend computes the next occurrence, so skip the check for them.
    if (occasionType !== 'birthday' && occasionType !== 'anniversary' && !FIXED_DATES[occasionType] && resolvedDate < today) {
      setError('Please choose a date in the future.'); return;
    }

    // Enforce 14-day fulfillment lead time for one-time (non-recurring) cards.
    // Recurring occasions are allowed through — the backend auto-advances to next year.
    if (!recurring && !FIXED_DATES[occasionType] && occasionType !== 'birthday') {
      if (resolvedDate < minScheduleDate) {
        setError(
          `We need at least ${LEAD_DAYS} days to print and mail your card. ` +
          `The earliest date you can schedule is ${formatDateLong(minScheduleDate)}.`,
        );
        return;
      }
    }

    setSaving(true);
    try {
      // Persist the sign-off (closing + name) to the profile so every card uses
      // it. Best-effort: a failure here must not block the occasion from saving.
      const trimmedFrom = cardFromLine.trim();
      if (cardClosing !== (profile?.card_closing ?? 'With love,') ||
          trimmedFrom !== (profile?.card_from_line ?? '') ||
          cardFontId  !== (profile?.card_font_id ?? DEFAULT_CARD_FONT_ID)) {
        try {
          await apiFetch('/api/auth/profile', {
            method: 'PATCH',
            body: JSON.stringify({ cardClosing, cardFromLine: trimmedFrom, cardFontId }),
          });
          await refreshProfile();
        } catch { /* non-fatal */ }
      }

      const parsedBirthYear = birthYear ? parseInt(birthYear, 10) : NaN;
      const validBirthYear  = !isNaN(parsedBirthYear) && parsedBirthYear > 1900 && parsedBirthYear <= new Date().getFullYear()
        ? parsedBirthYear
        : undefined;

      const json = await apiFetch<{ occasion: Occasion; warning?: string | null }>('/api/occasions', {
        method: 'POST',
        body: JSON.stringify({
          contact_id:           contactId,
          occasion_type:        occasionType,
          occasion_date:        resolvedDate,
          recurring,
          custom_occasion_name: occasionType === 'custom' ? customName.trim() : undefined,
          life_moment_notes:    lifeMomentNotes.trim() || undefined,
          card_design_id:       cardDesignId ?? undefined,
          birth_year:           occasionType === 'birthday' ? validBirthYear : undefined,
          card_greeting:        cardGreeting.trim() || undefined,
          current_message:      currentMessage.trim() || undefined,
        }),
      });
      if (json.warning) {
        setSavedOccasion(json.occasion);
        setScheduleNotice(json.warning);
      } else {
        onSave(json.occasion);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>

      {/* Occasion type grid */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-2">
          What are we celebrating?
        </label>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {(Object.entries(OCCASION_TYPES) as [OccasionType, OccasionConfig][]).map(([type, cfg]) => {
            const locked = isLocked(type);
            return (
              <button
                key={type}
                type="button"
                onClick={() => {
                  if (locked) {
                    navigate('/pricing?reason=occasion_type_limit');
                  } else {
                    handleTypeChange(type);
                  }
                }}
                title={locked ? 'Upgrade to unlock this occasion type' : undefined}
                className={`relative flex flex-col items-center gap-1 rounded-xl px-2 py-3 text-xs font-body border transition-all ${
                  locked
                    ? 'bg-gray-50 border-gray-100 text-gray-300 cursor-pointer'
                    : occasionType === type
                      ? 'bg-sage/10 border-sage text-sage font-semibold'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-sage/50'
                }`}
              >
                <span className="text-xl leading-none">{locked ? '🔒' : cfg.emoji}</span>
                <span className="text-center leading-tight">{cfg.label}</span>
                {locked && (
                  <span className="text-[8px] font-semibold text-amber-500 leading-tight">Upgrade</span>
                )}
              </button>
            );
          })}
        </div>
        {config.description && (
          <p className="mt-2 text-xs text-gray-400 font-body">{config.description}</p>
        )}
      </div>

      {/* Address warning — shown when we know the contact has no saved address */}
      {hasAddress === false && (
        <div className="rounded-xl border px-4 py-3 flex gap-3 items-start"
             style={{ backgroundColor: '#FFFBEB', borderColor: '#FDE68A' }}>
          <span className="text-lg leading-none mt-0.5">📍</span>
          <div>
            <p className="font-body text-sm font-semibold" style={{ color: '#92400E' }}>
              No delivery address saved
            </p>
            <p className="font-body text-xs mt-0.5" style={{ color: '#92400E' }}>
              {contactName} doesn't have a mailing address yet. Add one in their contact details so
              we know where to send the card.
            </p>
          </div>
        </div>
      )}

      {/* Custom occasion name */}
      {occasionType === 'custom' && (
        <div>
          <label className="block font-body text-sm font-medium text-navy mb-1.5">
            Occasion name <span style={{ color: '#c0534a' }}>*</span>
          </label>
          <input
            type="text"
            placeholder="e.g. Work promotion, New home"
            value={customName}
            onChange={(e) => setCustomName(e.target.value)}
            disabled={saving}
            className="input-field w-full"
          />
        </div>
      )}

      {/* Date — birthday uses month/day selects; everything else uses a date picker */}
      {!FIXED_DATES[occasionType] && (
        occasionType === 'birthday' ? (
          <div>
            <label className="block font-body text-sm font-medium text-navy mb-1.5">
              Birthday <span style={{ color: '#c0534a' }}>*</span>
            </label>
            <div className="flex gap-2">
              <select
                value={birthMonth}
                onChange={(e) => setBirthMonth(e.target.value)}
                disabled={saving}
                className="input-field flex-1"
              >
                <option value="">Month</option>
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
              <select
                value={birthDay}
                onChange={(e) => setBirthDay(e.target.value)}
                disabled={saving}
                className="input-field w-24"
              >
                <option value="">Day</option>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                  <option key={d} value={String(d).padStart(2, '0')}>{d}</option>
                ))}
              </select>
            </div>
            <p className="mt-1 text-xs text-gray-400 font-body">
              Month and day is all we need. Add the birth year below only if you want milestone notifications.
            </p>
          </div>
        ) : (
          <div>
            <label className="block font-body text-sm font-medium text-navy mb-1.5">
              {ONE_TIME_DEFAULT_TYPES.includes(occasionType) && !recurring
                ? 'When should it arrive?'
                : config.showYear ? 'Date' : 'Month and day'}{' '}
              <span style={{ color: '#c0534a' }}>*</span>
            </label>
            <input
              type="date"
              value={occasionDate}
              min={!recurring ? minScheduleDate : undefined}
              onChange={(e) => setOccasionDate(e.target.value)}
              disabled={saving}
              className="input-field w-full"
            />
            {/* One-time: explain lead time and why early dates are grayed out */}
            {ONE_TIME_DEFAULT_TYPES.includes(occasionType) && !recurring && (
              <div className="mt-2 rounded-lg border px-3 py-2 flex gap-2 items-start"
                   style={{ backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                <span className="text-sm leading-none mt-0.5">📅</span>
                <p className="font-body text-xs" style={{ color: '#166534' }}>
                  Dates before <strong>{formatDateLong(minScheduleDate)}</strong> are unavailable.
                  We need at least 14 days to print, address, and mail your card.
                </p>
              </div>
            )}
            {/* Recurring: soft notice when this year's date is within the lead window */}
            {recurring && occasionDate && occasionDate < minScheduleDate && (
              <p className="mt-1 text-xs font-body" style={{ color: '#92400E' }}>
                This year's date is less than {LEAD_DAYS} days away, your first card will
                automatically be scheduled for next year.
              </p>
            )}
          </div>
        )
      )}

      {/* Birth year — birthday only, fully optional */}
      {occasionType === 'birthday' && (
        <div>
          <label className="block font-body text-sm font-medium text-navy mb-1.5">
            Birth year <span className="font-normal text-gray-400">(optional)</span>
          </label>
          <input
            type="number"
            placeholder="e.g. 1985"
            value={birthYear}
            onChange={(e) => setBirthYear(e.target.value)}
            min={1900}
            max={new Date().getFullYear()}
            disabled={saving}
            className="input-field w-full"
          />
          <p className="mt-1 text-xs text-gray-400 font-body">
            Only needed if you want us to flag milestone birthdays like their 40th or 50th. Skip it if you're not sure.
          </p>
        </div>
      )}

      {/* Recurring toggle — only for one-time-capable types */}
      {config.showYear && !FIXED_DATES[occasionType] && (
        <div className="flex items-center justify-between rounded-xl bg-gray-50 px-4 py-3">
          <div>
            <p className="font-body text-sm font-medium text-navy">Repeat every year</p>
            <p className="font-body text-xs text-gray-400 mt-0.5">
              {recurring ? `We'll send a card every year on this date` : 'Send one card only'}
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={recurring}
            onClick={() => setRecurring(!recurring)}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              recurring ? 'bg-sage' : 'bg-gray-300'
            }`}
          >
            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
              recurring ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      )}

      {/* Card design picker */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="font-body text-sm font-medium text-navy">
            Card design <span className="font-normal text-gray-400">(optional)</span>
          </label>
        </div>
        <CardDesignPicker
          occasionType={occasionType}
          selected={cardDesignId}
          onChange={setCardDesignId}
          disabled={saving}
          cardClosing={profile?.card_closing ?? 'With love,'}
          cardFromLine={profile?.card_from_line ?? profile?.first_name ?? ''}
          cardFontId={cardFontId}
        />
        <p className="mt-1.5 text-xs text-gray-400 font-body">
          Leave on "We pick" and we'll choose a style that matches the occasion.
        </p>
      </div>

      {/* Greeting */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-1.5">
          Card greeting <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          type="text"
          placeholder={greetingPlaceholder(occasionType)}
          value={cardGreeting}
          onChange={(e) => setCardGreeting(e.target.value)}
          disabled={saving}
          maxLength={120}
          className="input-field w-full"
        />
        <p className="mt-1 text-xs text-gray-400 font-body">
          The opening line printed inside your card.
        </p>
      </div>

      {/* Personal message */}
      {/* Cap matches the other card composers. The template's inside box holds
          about 14 lines (render-verified); a longer message would overflow it. */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-1.5">
          Your message <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <textarea
          placeholder="Write something personal…"
          value={currentMessage}
          onChange={(e) => setCurrentMessage(e.target.value)}
          disabled={saving}
          rows={4}
          maxLength={650}
          className="input-field w-full resize-none"
        />
        <div className="mt-1 flex items-start justify-between gap-3">
          <p className="text-xs text-gray-400 font-body">
            {recurring
              ? "We'll remind you to update this each year, 14 days before the card mails."
              : 'Add your personal note now so the card is ready to send.'}
          </p>
          <p
            className={`text-xs font-body whitespace-nowrap ${
              currentMessage.length >= 650 ? 'text-rose-dark font-medium' : 'text-gray-400'
            }`}
          >
            {currentMessage.length}/650
          </p>
        </div>
      </div>

      {/* Sign-off: closing line + name, printed at the bottom of the card */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-1.5">
          Sign-off
        </label>
        <div className="flex flex-col gap-2">
          <input
            type="text"
            value={cardClosing}
            onChange={(e) => setCardClosing(e.target.value)}
            disabled={saving}
            maxLength={60}
            placeholder="With love,"
            className="input-field w-full"
          />
          <div className="flex flex-wrap gap-1.5">
            {CLOSING_OPTIONS.map((chip) => (
              <button
                key={chip}
                type="button"
                disabled={saving}
                onClick={() => setCardClosing(chip)}
                className={`font-body text-xs px-2.5 py-1 rounded-full border transition-colors ${
                  cardClosing === chip
                    ? 'border-sage bg-sage/15 text-navy'
                    : 'border-gray-200 text-gray-500 hover:border-sage/50'
                }`}
              >
                {chip}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={cardFromLine}
            onChange={(e) => setCardFromLine(e.target.value)}
            disabled={saving}
            maxLength={60}
            placeholder="Your name"
            className="input-field w-full"
          />
        </div>
        <p className="mt-1.5 text-xs text-gray-400 font-body">
          Printed at the bottom of the card, e.g. "{cardClosing || 'With love,'} {cardFromLine || profile?.first_name || 'You'}". Saved for all your cards.
        </p>
      </div>

      {/* Handwriting style: the font the inside message prints in */}
      <div>
        <label className="block font-body text-sm font-medium text-navy mb-1.5">
          Handwriting style
        </label>
        <FontPicker value={cardFontId} onChange={setCardFontId} disabled={saving} />
        <p className="mt-1.5 text-xs text-gray-400 font-body">
          Pick the style closest to your own handwriting. Used for every card you send.
        </p>
      </div>

      {/* Schedule notice (occasion too close — shown instead of auto-closing) */}
      {scheduleNotice && savedOccasion && (
        <div className="rounded-xl border px-4 py-3 space-y-3"
             style={{ backgroundColor: '#FFFBEB', borderColor: '#FDE68A' }}>
          <p className="font-body text-sm" style={{ color: '#92400E' }}>{scheduleNotice}</p>
          <button
            type="button"
            onClick={() => onSave(savedOccasion)}
            className="btn-primary w-full"
          >
            Got it, continue
          </button>
        </div>
      )}

      {/* Error */}
      {!scheduleNotice && error && (
        <p className="text-sm font-body text-center" style={{ color: '#c0534a' }}>{error}</p>
      )}

      {/* Actions */}
      {!scheduleNotice && <div className="flex gap-3 pt-1">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={saving}
            className="btn-secondary flex-1"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={saving}
          className="btn-primary flex-1 disabled:opacity-60"
        >
          {saving ? 'Saving…' : submitLabel}
        </button>
      </div>}
    </form>
  );
}
